// third-party
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
    createCatalogVendorsAccessService,
    createProfileService,
    deleteMenusService,
    getApprovalProfilesService,
    getMenuPermissionsService,
    getProfilesService,
    getUsersListService,
    getVendorsAccessService,
    recoverPasswordService,
    registerUserService,
    updateNumberService,
    updateProfileService,
    updateUserInfoService
} from 'services/user';
import { openSnackbar } from './snackbar';

// types
import { DefaultRootStateProps } from 'types';
import {
    NewProfileType,
    RecoverPasswordProps,
    RegisterUserServiceProps,
    MenuDetailsType,
    ProfileType,
    UserType
} from 'services/users.types';

// ----------------------------------------------------------------------

const initialState: DefaultRootStateProps['users'] = {
    error: null,
    loading: true,
    usersList: [],
    loadingEditInfo: true,
    profiles: [],
    approvalProfiles: [],
    fetching: false,
    menuOptions: [],
    vendorsAccess: []
};

const slice = createSlice({
    name: 'security',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },
        basicSuccess(state) {
            state.loading = false;
        },
        // USERS

        getUsersListPending(state) {
            state.loading = true;
        },
        getUsersListSuccess(state, action) {
            state.usersList = action.payload.map((item: UserType) => ({
                ...item,
                name: `${item.firstName} ${item.lastName ?? ''}`.trim(),
                avatart: 'default-profile.jpg'
            }));
            state.loading = false;
        },
        getUserDataPending(state) {
            state.loading = true;
        },
        getUserDataSuccess(state, action) {
            state.loading = false;
            // state.currentUser = action.payload;
        },
        // profiles
        getEditInfoPending(state) {
            state.loadingEditInfo = true;
        },
        getApprovalProfilesSuccess(state, action) {
            state.loadingEditInfo = false;
            state.approvalProfiles = action.payload;
        },
        // profile => edit
        updateProfilePending(state) {
            state.fetching = true;
        },
        updateProfileSuccess(state) {
            state.fetching = false;
        },
        deleteMenusServicePending(state) {
            state.fetching = true;
        },
        deleteMenusServiceSuccess(state) {
            state.fetching = false;
        },
        // user info
        updateUserInfoPending(state) {
            state.fetching = true;
        },
        updateUserInfoSuccess(state) {
            state.fetching = false;
        },
        // menu => access
        getMenuPermissionsSuccess(state, action) {
            const optionsList: MenuDetailsType[] = [];
            const payloadResponse: MenuDetailsType[] = action.payload;

            payloadResponse.forEach((optionItem) => {
                const exist = optionsList.some((item) => item.id === optionItem.id);
                if (!exist) {
                    optionsList.push(optionItem);
                }
            });

            state.menuOptions = optionsList;
            state.loading = false;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(getProfiles.pending, (state) => {
                state.loading = true;
            })
            .addCase(getProfiles.fulfilled, (state, action) => {
                state.loading = false;

                const profiles: ProfileType[] = action.payload;
                state.profiles = profiles;
            });
        builder
            .addCase(getVendorsAccess.pending, (state) => {
                state.loading = true;
            })
            .addCase(getVendorsAccess.fulfilled, (state, action) => {
                state.loading = false;
                state.vendorsAccess = action.payload;
            });
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------
export const { hasError } = slice.actions;

export const getUsersList = createAsyncThunk(`${slice.name}/getUsersList`, async (idMerchant: string | undefined, { dispatch }) => {
    dispatch(slice.actions.getUsersListPending());
    try {
        const { data } = await getUsersListService({ idMerchant });

        dispatch(slice.actions.getUsersListSuccess(data.response));
        return data.response;
    } catch (error) {
        dispatch(slice.actions.hasError(error));
        throw error;
    }
});

export const registerUserSuccess = createAsyncThunk(
    `${slice.name}/registerUserSuccess`,
    async (data: RegisterUserServiceProps, { dispatch }) => {
        try {
            await registerUserService(data);
            dispatch(openSnackbar({ message: 'User Successfully created', variant: 'success' }));
        } catch (error: any) {
            const msg =
                error?.response?.data?.code === 'auth/email-already-exists'
                    ? 'The email address is already in use by another account.'
                    : 'An error occurred registering a new user';
            dispatch(openSnackbar({ message: msg, variant: 'error' }));
            throw error;
        }
    }
);

export const getProfiles = createAsyncThunk(`${slice.name}/getProfiles`, async ({ idMerchant }: { idMerchant?: number | string }) => {
    const response = await getProfilesService(idMerchant);
    return response.data.response;
});

export const getApprovalProfiles = createAsyncThunk(
    `${slice.name}/getApprovalProfiles`,
    async (idMerchant: number | undefined, { dispatch }) => {
        dispatch(slice.actions.getEditInfoPending());
        try {
            const response = await getApprovalProfilesService(idMerchant);
            // const response = await axios.get(`/loolu/api/product/approvalprofile`, { params: { idMerchant } });
            dispatch(slice.actions.getApprovalProfilesSuccess(response.data.response));
            return response.data.response;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    }
);
export const updateUserInfo = createAsyncThunk(`${slice.name}/updateUserInfo`, async (dataSend: any) => {
    const response = await updateUserInfoService(dataSend);
    return response.data;
});

export const updateNumber = createAsyncThunk(`${slice.name}/updateNumber`, async (props: RegisterUserServiceProps, { dispatch }) => {
    try {
        await updateNumberService(props);
        dispatch(openSnackbar({ message: 'User Successfully updated', variant: 'success' }));
    } catch (error) {
        dispatch(openSnackbar({ message: 'An error occurred updating user', variant: 'error' }));
        throw error;
    }
});

export const recoverPassword = createAsyncThunk(`${slice.name}/recoverPassword`, async (props: RecoverPasswordProps, { dispatch }) => {
    try {
        await recoverPasswordService(props);
        dispatch(openSnackbar({ message: 'Email sent, check your email.', variant: 'success' }));
    } catch (error) {
        dispatch(slice.actions.hasError(error));
        dispatch(openSnackbar({ message: 'An error occurred while sending the reset email', variant: 'error' }));
        throw error;
    }
});

export const getMenuPermissions = createAsyncThunk(
    `${slice.name}/getMenuPermissions`,
    async (idMerchant: number | undefined, { dispatch }) => {
        try {
            const response = await getMenuPermissionsService(idMerchant ?? 1);
            dispatch(slice.actions.getMenuPermissionsSuccess(response.data.response));
            return response.data.response;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    }
);
export const createProfileThunk = createAsyncThunk(`${slice.name}/createProfileService`, async (data: NewProfileType) => {
    const response = await createProfileService(data);
    return response.data;
});
export const getVendorsAccess = createAsyncThunk(`${slice.name}/getVendorsAccess`, async () => {
    const response = await getVendorsAccessService();

    console.log('response', response);
    return response.data;
});

export const createCatalogVendorsAccess = createAsyncThunk(
    `${slice.name}/createCatalogVendorsAccess`,
    async ({ id, action, IdPerfil, ID }: { id: number; action: string; IdPerfil: number; ID: number }) => {
        const response = await createCatalogVendorsAccessService({ action: action, IdPerfil: IdPerfil, ID: ID, id });
        return response.data;
    }
);

type Menus = {
    id: number;
    read: boolean;
    write: boolean;
};
type updateProfileServiceProps = {
    data: {
        idPerfil: number;
        idStatus: boolean;
        description: string;
        menus: Menus[];
        type: string;
    };
    idMerchant?: number;
};

export const updateProfileThunk = createAsyncThunk(
    `${slice.name}/updateProfileService`,
    async ({ data, idMerchant }: updateProfileServiceProps) => {
        const response = await updateProfileService(data, idMerchant);
        return response.data;
    }
);

type deleteMenusServiceProps = {
    menus: number[];
    idPerfil: number;
    idMerchant?: number;
};
export const deleteMenusThunk = createAsyncThunk(
    `${slice.name}/deleteMenusService`,
    async ({ menus, idPerfil, idMerchant }: deleteMenusServiceProps, { dispatch, rejectWithValue }) => {
        try {
            await deleteMenusService(menus, idPerfil, idMerchant);
            dispatch(slice.actions.deleteMenusServiceSuccess());
        } catch (error: any) {
            dispatch(slice.actions.hasError(error));
            return rejectWithValue(error); // permite manejar el error en el componente
        }
    }
);
